// Where the hrth server is. In the browser the page comes from the server, so everything
// is same-origin and the base is ''. The apps (Tauri, apps/tauri) bundle this same build
// and load it from their own origin: there the server is the one chosen on first run
// (Connect view), and every API, WebSocket and font URL is prefixed with it.

const SERVER_KEY = 'hrth.server'

/** Running inside the Tauri apps (detected at runtime: one web build serves both). */
export const isApp = typeof globalThis !== 'undefined' && '__TAURI_INTERNALS__' in globalThis

/** The phone apps (Android, iOS), as opposed to the desktop app. */
export const isMobileApp = isApp && /Android|iPhone|iPad/i.test(globalThis.navigator?.userAgent ?? '')

function read(): string {
  try {
    return globalThis.localStorage?.getItem(SERVER_KEY) ?? ''
  } catch {
    return ''
  }
}

/** The server's origin (`https://chat.example.org`), or '' for the page's own origin. */
export function serverBase(): string {
  return isApp ? read() : ''
}

/** In the apps: has a server been chosen yet? (Always true in the browser.) */
export function hasServer(): boolean {
  return !isApp || read() !== ''
}

export function setServer(origin: string): void {
  globalThis.localStorage?.setItem(SERVER_KEY, origin)
}

/** Back to the Connect screen (only while signed out: this device's keys are for that server). */
export function forgetServer(): void {
  globalThis.localStorage?.removeItem(SERVER_KEY)
}

/** `/api/v1<path>` on the server. */
export function apiUrl(path: string): string {
  return `${serverBase()}/api/v1${path}`
}

/** The server's host[:port], as people type it (federation, git remotes). */
export function serverHost(): string {
  const base = serverBase()
  return base ? new URL(base).host : globalThis.location.host
}

/**
 * The current page as the server's URL: in the apps the page's own origin (tauri://…)
 * means nothing to anyone else, so links read from it (invites with their #key) are
 * rebuilt on the server's origin.
 */
export function pageUrl(): string {
  const { pathname, search, hash, href } = globalThis.location
  const base = serverBase()
  return base ? `${base}${pathname}${search}${hash}` : href
}

/**
 * Reads what someone typed or pasted on the Connect screen: a server (`chat.example.org`,
 * `https://chat.example.org:8443/`) or any link to a page on it (an invite, a device link).
 * Returns the origin, and the in-app path to continue to; null when it isn't a URL.
 * Bare hosts get https, except localhost and IP addresses (local test servers).
 */
export function parseServerInput(input: string): { origin: string; path: string } | null {
  let s = input.trim()
  if (!s) return null
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(s)) {
    const host = s.split(/[/?#]/)[0]!
    const local = /^(localhost|127\.|10\.|192\.168\.|\[?::1\]?)/i.test(host) || /^[\d.]+(:\d+)?$/.test(host)
    s = `${local ? 'http' : 'https'}://${s}`
  }
  let url: URL
  try {
    url = new URL(s)
  } catch {
    return null
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null
  if (!url.hostname.includes('.') && url.hostname !== 'localhost' && !url.hostname.startsWith('[')) return null
  const path = `${url.pathname}${url.search}${url.hash}`
  return { origin: url.origin, path: path === '/' ? '/' : path }
}

/**
 * Deep links into the apps: `hrth://<host>/<path>` is that page on the server (an invite,
 * a device link…), over https as for a typed host. Returns the same shape as parseServerInput.
 */
export function parseDeepLink(link: string): { origin: string; path: string } | null {
  const m = link.trim().match(/^hrth:\/\/(.+)$/i)
  return m ? parseServerInput(m[1]!) : null
}

interface TauriInternals {
  invoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T>
}

/** Calls a command of the app shell (apps/tauri/src/lib.rs). Only in the apps. */
export function appInvoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T> {
  return (globalThis as unknown as { __TAURI_INTERNALS__: TauriInternals }).__TAURI_INTERNALS__.invoke<T>(cmd, args)
}

/**
 * Where a deep link takes the app: its page when it's on this server (or no server is
 * chosen yet: then it becomes the server); an invite to a space on another server joins it
 * from this one, as the browser does (JoinRemote). Null: nothing to open.
 */
export function deepLinkTarget(link: string, current = serverBase()): { server?: string; path: string } | null {
  const t = parseDeepLink(link)
  if (!t) return null
  if (!current) return { server: t.origin, path: t.path }
  if (t.origin === current) return { path: t.path }
  if (t.path.startsWith('/i/')) return { path: `/join#invite=${encodeURIComponent(t.origin + t.path)}` }
  return null
}
